/**
 * Filter shape + constants + helpers shared between `QuestionsPage`
 * (which owns the state) and `QuestionListPanel` (which renders the
 * filter controls). Extracted during the 2026-05-17 split-pane refactor.
 */

export interface QuestionFilters {
    subject: string;
    moduleId: string;
    difficulty: string;
    questionType: string;
    grade: string;
    targetExam: string;
    importStatus: string;
    /** Origin filter (question-pools-and-entitlement/question-source-type-target.md) — matches `sourceType`. Admin-only. */
    sourceType: string;
    enrichStatus: string;
    readiness: string;
    /** Inclusive "from"/"to" calendar dates (YYYY-MM-DD) for when the question
     *  was created (`createdAt`). Empty string = unbounded on that end. */
    dateAddedFrom: string;
    dateAddedTo: string;
    /** Inclusive "from"/"to" calendar dates (YYYY-MM-DD) for when the question
     *  was last modified (`_ts`). Empty string = unbounded on that end. */
    dateModifiedFrom: string;
    dateModifiedTo: string;
    search: string;
    hasDiagramIn: string;
    /** Multi-select exam filter scoped to `appearedInExams[].exam` only
     *  (distinct from the unified `targetExam` filter that matches either
     *  `targetExams` OR `appearedInExams`). Comma-separated. */
    appearedInExam: string;
    /** Year filter scoped to `appearedInExams[].date` — matches via
     *  STARTSWITH(date, "<year>-"). Single year string, e.g. "2024". */
    appearedInYear: string;
    /** Specific exam date filter — exact match on `appearedInExams[].date`.
     *  ISO date string, e.g. "2024-01-27". */
    appearedOnDate: string;
    /** Session filter — case-insensitive substring match on
     *  `appearedInExams[].session` (free-form, e.g. "Shift 1", "Set 56/2/1"). */
    appearedInSession: string;
}

export const EMPTY_FILTERS: QuestionFilters = {
    subject: '', moduleId: '', difficulty: '', questionType: '', grade: '',
    targetExam: '', importStatus: '', sourceType: '', enrichStatus: '', readiness: '',
    dateAddedFrom: '', dateAddedTo: '', dateModifiedFrom: '', dateModifiedTo: '',
    search: '', hasDiagramIn: '',
    appearedInExam: '', appearedInYear: '', appearedOnDate: '', appearedInSession: '',
};

export const DIAGRAM_LOCATIONS: { value: string; label: string }[] = [
    { value: 'question', label: 'Question' },
    { value: 'option', label: 'Option' },
    { value: 'solution', label: 'Solution' },
];

/** Filter keys living in the "Advanced filters" accordion. Kept in one place so
 *  the active-count badge and the "Clear" reset stay in sync with the UI. */
export const ADVANCED_FILTER_KEYS = [
    'hasDiagramIn', 'enrichStatus', 'readiness',
    'dateAddedFrom', 'dateAddedTo', 'dateModifiedFrom', 'dateModifiedTo',
    'appearedInExam', 'appearedInYear', 'appearedOnDate', 'appearedInSession',
] as const satisfies ReadonlyArray<keyof QuestionFilters>;

/** Return a copy of `f` with every advanced filter blanked (all other filters
 *  untouched). Used by the "Clear" affordance in the advanced-filter accordion. */
export function clearAdvancedFilters(f: QuestionFilters): QuestionFilters {
    const next = { ...f };
    for (const key of ADVANCED_FILTER_KEYS) next[key] = '';
    return next;
}

/** Readiness values flagged as "exclusive" in the MultiSelectFilter — picking
 *  them deselects the others (and vice versa). Today only "rejected" (-1)
 *  is exclusive; kept as a constant so the list panel and any other consumer
 *  share the same set. */
export const REJECTED_EXCLUSIVE = new Set(['-1']);

/** Parse a `YYYY-MM-DD` string into [year, month, day] numbers, or null if invalid. */
function parseYmd(d: string): [number, number, number] | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    if (!m) return null;
    return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** Epoch seconds for the start of a `YYYY-MM-DD` day in the viewer's local
 *  timezone (00:00:00.000 local). Returns null for empty/invalid input. */
function dayStartEpoch(d: string): number | null {
    const ymd = parseYmd(d);
    if (!ymd) return null;
    const ms = new Date(ymd[0], ymd[1] - 1, ymd[2], 0, 0, 0, 0).getTime();
    return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

/** Epoch seconds for the end of a `YYYY-MM-DD` day in the viewer's local
 *  timezone (23:59:59.999 local). Returns null for empty/invalid input. */
function dayEndEpoch(d: string): number | null {
    const ymd = parseYmd(d);
    if (!ymd) return null;
    const ms = new Date(ymd[0], ymd[1] - 1, ymd[2], 23, 59, 59, 999).getTime();
    return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

export function filtersEqual(a: QuestionFilters, b: QuestionFilters): boolean {
    return Object.keys(EMPTY_FILTERS).every(
        k => a[k as keyof QuestionFilters] === b[k as keyof QuestionFilters],
    );
}

/** Build the API filter map sent to `api.getQuestionList`. Admin-only and
 *  status-filter-only fields are gated here so the list-panel UI can include
 *  the controls unconditionally and have them be no-ops for non-admins. */
export function buildApiFilters(
    f: QuestionFilters,
    isAdmin: boolean,
    serverTag?: string,
    showStatusFilter?: boolean,
): Record<string, string | number> {
    // Note: `/question-list` only reads `page` + `pageSize`. `maxCount` is a
    // leftover from an older flat-ceiling contract and is ignored server-side.
    // App wrappers add `pageSize: 200`.
    const result: Record<string, string | number> = {};
    if (f.subject) result.subject = f.subject;
    if (f.moduleId) result.moduleId = f.moduleId;
    if (f.difficulty) result.difficulty = f.difficulty;
    if (f.questionType) result.questionType = f.questionType;
    if (f.grade) result.grade = f.grade;
    if (f.targetExam) result.targetExam = f.targetExam;  // comma-separated for multi-select
    // Trimmed here rather than in the input's state, which would swallow the
    // space between words as they are typed. A pasted question id routinely
    // carries a leading/trailing space or newline, and the server matches the
    // string literally — so an untrimmed value just returns nothing. A
    // whitespace-only box counts as no search at all.
    const search = f.search.trim();
    if (search) result.searchString = search;
    // Date Added range → `createdAfter`/`createdBefore` (ISO, matched against
    // the `createdAt` ISO string server-side).
    const addedFrom = dayStartEpoch(f.dateAddedFrom);
    if (addedFrom !== null) result.createdAfter = new Date(addedFrom * 1000).toISOString();
    const addedTo = dayEndEpoch(f.dateAddedTo);
    if (addedTo !== null) result.createdBefore = new Date(addedTo * 1000).toISOString();
    // Date Modified range → `modifiedAfter`/`modifiedBefore` (epoch seconds,
    // matched against the Cosmos `_ts` timestamp server-side).
    const modifiedFrom = dayStartEpoch(f.dateModifiedFrom);
    if (modifiedFrom !== null) result.modifiedAfter = String(modifiedFrom);
    const modifiedTo = dayEndEpoch(f.dateModifiedTo);
    if (modifiedTo !== null) result.modifiedBefore = String(modifiedTo);
    if (f.importStatus && (isAdmin || showStatusFilter)) result.importStatus = f.importStatus;
    if (isAdmin) {
        if (f.sourceType) result.sourceType = f.sourceType;
        if (f.enrichStatus) result.enrichStatus = f.enrichStatus;
        if (f.readiness) result.readiness = f.readiness;
    }
    if (f.hasDiagramIn) result.hasDiagramIn = f.hasDiagramIn;
    if (f.appearedInExam) result.appearedInExam = f.appearedInExam;
    if (f.appearedInYear) result.appearedInYear = f.appearedInYear;
    if (f.appearedOnDate) result.appearedOnDate = f.appearedOnDate;
    if (f.appearedInSession) result.appearedInSession = f.appearedInSession;
    if (serverTag) result.tag = serverTag;
    return result;
}
